// Renders AgentHub's HITL interrupts (user_question / approval_requested).
// Submission goes through the runtime-state context, which posts a `resume`
// entry through the same native agent endpoint:
// it POSTs the answer back to the same agent/thread with a `resume` entry,
// and the backend applies it to the pending interaction. This
// component only renders what the backend sent and collects the answer; it
// never invents questions of its own.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/lib/cn';
import {
  useAgenthubInterrupts,
  useAgenthubRestartConversation,
  useAgenthubRunExpired,
  useAgenthubSubmitInterruptResponses,
  type AgenthubInterrupt,
} from '@/shared/chat-runtime/agenthubInterrupts';
import { normalizeUserQuestionPayload } from '../model/normalizeUserQuestion';

type AnswersState = Record<string, string | string[]>;

function toggleMultiValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

// Distinct from the empty-state ThreadPrimitive.Suggestion prompt chips: this
// pauses run execution on a structured tool-call interrupt and requires a
// structured answer/approval, not a one-tap starter prompt.
export function InterruptPanel({ interrupt: provided, compact = false, disabled = false, onResolved }: { interrupt?: AgenthubInterrupt; compact?: boolean; disabled?: boolean; onResolved?: () => void } = {}) {
  const globalInterrupts = useAgenthubInterrupts();
  const submitInterruptResponses = useAgenthubSubmitInterruptResponses();
  const runExpired = useAgenthubRunExpired();
  const restartConversation = useAgenthubRestartConversation();
  // A specialist card supplies its own interrupt; without one this renders the
  // thread-global slot as before.
  const interrupt = provided ?? globalInterrupts[0] ?? null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // React state is committed after the current event handler. Keep a
  // synchronous lock as well so a rapid double click cannot submit the same
  // durable pause twice: the first continuation may complete before the
  // second POST reaches the backend, where it would misleadingly return 404.
  const submittingRef = useRef(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [freeText, setFreeText] = useState('');

  const question = useMemo(() => {
    if (!interrupt) return null;
    const data = readInterruptData(interrupt);
    return normalizeUserQuestionPayload({
      ...data,
      question: data.question ?? interrupt.message,
      type: interrupt.reason,
      tool_call_id: interrupt.toolCallId,
      id: interrupt.id,
    });
  }, [interrupt]);

  const initialAnswers = useMemo<AnswersState>(() => {
    const next: AnswersState = {};
    for (const item of question?.questions ?? []) next[item.question] = item.multiSelect ? [] : '';
    return next;
  }, [question]);
  const [answers, setAnswers] = useState<AnswersState>(initialAnswers);
  useEffect(() => {
    setAnswers(initialAnswers);
    setFreeText('');
  }, [initialAnswers, interrupt?.id]);

  if (!interrupt || !question) {
    // The run behind the last interrupt expired server-side (its own
    // wait-for-answer timeout fired) before the answer could be submitted —
    // there's no question left to render, but the user still needs to know
    // why their answer didn't go through and how to continue.
    if (!runExpired) return null;
    return (
    <div className="mx-auto my-2 flex w-full max-w-[70rem] flex-col gap-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground">Timed out</p>
          <p className="text-sm text-foreground">
            This conversation timed out waiting for your answer, so it can no longer continue. Restart to ask the
            same question again.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={isRestarting}
            onClick={() => {
              setIsRestarting(true);
              void restartConversation().finally(() => setIsRestarting(false));
            }}
          >
            Restart
          </Button>
        </div>
      </div>
    );
  }

  const isApproval = question.kind === 'approval';

  // `payload` shape matches AskUserQuestionAnswerSubmission / ApprovalAnswerSubmission
  // verbatim (src/agenthub/runtime/agents/claude/user_input.py,
  // src/agenthub/schemas/runs.py) — the backend validates it against those
  // same models regardless of whether it arrived via `resume` or the legacy
  // REST endpoints.
  async function resolve(payload: Record<string, unknown>) {
    if (submittingRef.current || isSubmitting || disabled) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await submitInterruptResponses([{
        interruptId: interrupt!.id,
        status: 'resolved',
        payload,
        // Names the exact question being answered. Without it the backend
        // resolves the newest pending event, which misroutes an answer when
        // two specialists are paused at once.
        ...(interrupt!.eventId ? { event_id: interrupt!.eventId } : {}),
      }]);
      onResolved?.();
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  function updateSingle(questionKey: string, value: string) {
    setAnswers((current) => ({ ...current, [questionKey]: value }));
  }
  function updateMulti(questionKey: string, value: string) {
    setAnswers((current) => {
      const selected = Array.isArray(current[questionKey]) ? (current[questionKey] as string[]) : [];
      return { ...current, [questionKey]: toggleMultiValue(selected, value) };
    });
  }
  async function submitStructured() {
    const normalizedAnswers = Object.fromEntries(
      Object.entries(answers).filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value.trim().length > 0)),
    );
    if (Object.keys(normalizedAnswers).length === 0 && freeText.trim().length === 0) return;
    await resolve({
      ...(Object.keys(normalizedAnswers).length > 0 ? { answers: normalizedAnswers } : {}),
      ...(freeText.trim().length > 0 ? { response: freeText.trim() } : {}),
    });
  }

  const questions = question.questions;
  const title = isApproval ? 'Approval required' : 'Clarification needed';
  const subtitle = isApproval
    ? 'Choose whether the agent can continue with this action.'
    : 'Answer below to start the next response.';

  return (
    <div className={cn('flex w-full flex-col gap-4 rounded-lg border bg-card', compact
      ? 'border-primary/40 p-3'
      : 'mx-auto my-2 max-w-[70rem] border-border p-4')}>
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold text-muted-foreground">{title}</p>
        <p className="text-sm text-foreground">{subtitle}</p>
      </div>

      {isApproval && question.toolName && (
        <p className="text-xs text-muted-foreground">Requested tool: {question.toolName}</p>
      )}

      {isApproval ? (
        <p className="text-sm">{question.approvalPrompt ?? question.question}</p>
      ) : questions.length > 0 ? (
        questions.map((item) => {
          const value = answers[item.question];
          const selectedSet = new Set(Array.isArray(value) ? value : []);
            return (
              <div key={item.question} className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                <p className="text-xs font-semibold text-muted-foreground">{item.header}</p>
                <p className="text-sm text-foreground">{item.question}</p>
                </div>
                {item.options.find((option) => option.preview)?.preview && (
                  <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs text-foreground">
                    {item.options.find((option) => option.preview)?.preview}
                  </pre>
                )}
                <div className="grid gap-2">
                  {item.options.map((option) => {
                  const optionValue = option.value ?? option.label;
                  const selected = item.multiSelect ? selectedSet.has(optionValue) : value === optionValue;
                  return (
                    <button
                      key={`${item.question}:${optionValue}`}
                      type="button"
                      disabled={isSubmitting || disabled}
                      onClick={() => {
                        // Single-select, single-question, no multi-select: submit
                        // immediately on click (the fast path most questions use).
                        // Every other shape (multi-select, multiple questions)
                        // stages the choice and waits for the submit button below,
                        // since more than one field needs to be filled in first.
                        if (questions.length === 1 && !item.multiSelect) {
                          void resolve({ answer: optionValue });
                          return;
                        }
                        if (item.multiSelect) updateMulti(item.question, optionValue);
                        else updateSingle(item.question, optionValue);
                      }}
                      className={cn(
                        'flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left text-sm transition-colors disabled:opacity-50',
                        selected
                          ? 'border-primary/40 bg-primary/10'
                          : 'border-border hover:bg-accent',
                      )}
                    >
                      <span>{option.label}</span>
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-sm text-foreground">{question.question}</p>
      )}

      {question.details && <p className="text-xs text-muted-foreground">{question.details}</p>}

      {/* Free text is always reachable, regardless of question shape — the
          Claude Agent SDK's own guidance for AskUserQuestion is to offer an
          "Other" style free-text answer alongside every set of options
          (see https://code.claude.com/docs/en/agent-sdk/user-input#support-free-text-input).
          It was previously hidden for the common single-question, single-select
          case, which made it unreachable for the most frequent question shape. */}
      <Textarea
        placeholder={isApproval ? 'Optional note for this approval' : 'Other / free-text answer'}
        value={freeText}
        onChange={(e) => setFreeText(e.target.value)}
        disabled={isSubmitting || disabled}
        rows={2}
      />
      <div className="flex justify-end gap-2">
        {isApproval ? (
          <>
            <Button
              variant="outline"
              disabled={isSubmitting || disabled}
              onClick={() => void resolve({ decision: 'denied', ...(freeText.trim() ? { reason: freeText.trim() } : {}) })}
            >
              {question.dismissLabel ?? 'Deny'}
            </Button>
            <Button
              disabled={isSubmitting || disabled}
              onClick={() => void resolve({ decision: 'approved', ...(freeText.trim() ? { reason: freeText.trim() } : {}) })}
            >
              {question.submitLabel ?? 'Approve'}
            </Button>
          </>
        ) : (
          <Button disabled={isSubmitting || disabled} onClick={() => void submitStructured()}>
            {question.submitLabel ?? 'Submit answer'}
          </Button>
        )}
      </div>
    </div>
  );
}

function readInterruptData(interrupt: AgenthubInterrupt): Record<string, unknown> {
  const metadata = interrupt.metadata;
  if (!metadata) return {};
  const data = metadata.data;
  return typeof data === 'object' && data !== null && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : metadata;
}
